// Lógica pura da árvore de pastas produto → item → etapa criada no Google Drive a partir da
// página de produção. Sem imports (nem sequer de outros módulos do projeto) para que o app
// React também possa importar este arquivo por caminho relativo, para uma prévia ao vivo do
// nome das pastas antes de confirmar a criação.

const CONTROL_CHARS = /[\x00-\x1F\x7F]/
const MAX_NAME_LENGTH = 255

/** Uppercase, sem espaços nas pontas, com sequências de espaço viradas em "-". */
export function formatStageAbbreviation(shortName: string): string {
  return shortName.trim().toUpperCase().replace(/\s+/g, "-")
}

/** "NN-ABREV" ou, com sufixo informado, "NN-ABREV-SUFIXO". */
export function buildStageFolderName(position: number, shortName: string, suffix: string): string {
  const nn = String(position).padStart(2, "0")
  const abbreviation = formatStageAbbreviation(shortName)
  const trimmedSuffix = suffix.trim()
  return trimmedSuffix ? `${nn}-${abbreviation}-${trimmedSuffix}` : `${nn}-${abbreviation}`
}

export interface ScaffoldStageInput {
  id: string
  position: number
  shortName: string
}

export interface ScaffoldItemInput {
  id: string
  name: string
  stages: ScaffoldStageInput[]
}

export interface BuildProductFolderScaffoldInput {
  productFolderName: string
  suffix: string
  items: ScaffoldItemInput[]
}

export interface ScaffoldStage {
  id: string
  folderName: string
}

export interface ScaffoldItem {
  id: string
  folderName: string
  stages: ScaffoldStage[]
}

export interface ProductFolderScaffold {
  productFolderName: string
  items: ScaffoldItem[]
}

/**
 * Monta os nomes de pasta da árvore <pasta raiz>/<produto>/<item>/<NN-ABREV-SUFIXO>, sem
 * tocar o Drive: cada item leva apenas as etapas informadas (item.stages), ordenadas por
 * posição.
 */
export function buildProductFolderScaffold(input: BuildProductFolderScaffoldInput): ProductFolderScaffold {
  return {
    productFolderName: input.productFolderName.trim(),
    items: input.items.map((item) => ({
      id: item.id,
      folderName: item.name.trim(),
      stages: [...item.stages]
        .sort((left, right) => left.position - right.position)
        .map((stage) => ({ id: stage.id, folderName: buildStageFolderName(stage.position, stage.shortName, input.suffix) })),
    })),
  }
}

function nameProblem(label: string, value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return `${label} não pode ficar vazio.`
  if (trimmed.includes("/")) return `${label} não pode conter "/".`
  if (CONTROL_CHARS.test(value)) return `${label} não pode conter caracteres de controle.`
  if (trimmed.length > MAX_NAME_LENGTH) return `${label} não pode ter mais de ${MAX_NAME_LENGTH} caracteres.`
  return null
}

/** Uppercase, sem espaços nas pontas, com espaços internos virando "-". Vazio para valores vazios/ausentes. */
function formatSuffixPart(value: string | undefined | null): string {
  if (!value) return ""
  return value.trim().toUpperCase().replace(/\s+/g, "-")
}

/** "MM-AA" a partir de um mês de referência "YYYY-MM" (ou "YYYY-MM-DD"). Vazio se não reconhecer o formato. */
function formatReferenceMonth(referenceMonth: string | undefined | null): string {
  const match = referenceMonth?.trim().match(/^(\d{4})-(\d{2})(?:-\d{2})?$/)
  if (!match) return ""
  const [, year, month] = match
  return `${month}-${year.slice(2)}`
}

const SHORT_IMPRINT_NAME_MAX_LENGTH = 20

/**
 * Escolhe o texto do selo no sufixo: o nome, quando é curto e não tem espaços (ex.: "Logos"),
 * ou o código do selo (ex.: "SLT"), quando o nome é composto (ex.: "Clube de Filosofia").
 */
function pickImprintSuffixPart(imprintName: string | undefined | null, imprintCode: string | undefined | null): string {
  const name = imprintName?.trim() ?? ""
  const isShortSingleWord = name.length > 0 && name.length <= SHORT_IMPRINT_NAME_MAX_LENGTH && !/\s/.test(name)
  if (isShortSingleWord) return formatSuffixPart(name)
  if (imprintCode?.trim()) return formatSuffixPart(imprintCode)
  return formatSuffixPart(name)
}

export interface BuildDefaultStageSuffixInput {
  productName: string
  sku?: string
  referenceMonth?: string
  imprintName?: string
  imprintCode?: string
}

/**
 * Sufixo padrão (editável) das pastas de etapa: <PRODUTO>-<SKU>-<MM-AA>-<SELO>, em maiúsculas,
 * pulando as partes que não estiverem disponíveis. Ex.: "CONSOLAÇÃO-B022-02-27-LOGOS".
 */
export function buildDefaultStageSuffix(input: BuildDefaultStageSuffixInput): string {
  const parts = [
    formatSuffixPart(input.productName),
    formatSuffixPart(input.sku),
    formatReferenceMonth(input.referenceMonth),
    pickImprintSuffixPart(input.imprintName, input.imprintCode),
  ].filter((part) => part.length > 0)
  return parts.join("-")
}

export interface ValidateScaffoldInput {
  productFolderName: string
  suffix: string
  items: Array<{ id: string; name: string; stages?: ScaffoldStageInput[] }>
}

/** Valida os dados de entrada da árvore de pastas e devolve as mensagens de erro (em português), se houver. */
export function validateScaffoldInput(input: ValidateScaffoldInput): string[] {
  const errors: string[] = []

  if (!input.productFolderName.trim()) errors.push("Informe o nome da pasta do produto.")
  else {
    const problem = nameProblem("O nome da pasta do produto", input.productFolderName)
    if (problem) errors.push(problem)
  }

  const suffixProblem = input.suffix.trim() ? nameProblem("O sufixo da pasta da etapa", input.suffix) : null
  if (suffixProblem) errors.push(suffixProblem)

  if (!input.items.length) errors.push("Selecione pelo menos um item da produção.")

  for (const item of input.items) {
    const problem = nameProblem(`O nome do item "${item.name.trim() || item.id}"`, item.name)
    if (problem) errors.push(problem)
    for (const stage of item.stages ?? []) {
      const stageName = buildStageFolderName(stage.position, stage.shortName, input.suffix)
      if (!Number.isSafeInteger(stage.position) || stage.position < 1 || !stage.shortName.trim()) {
        errors.push(`A etapa do item "${item.name.trim() || item.id}" tem posição ou abreviação inválida.`)
      } else {
        const stageProblem = nameProblem(`O nome da pasta da etapa "${stageName}"`, stageName)
        if (stageProblem) errors.push(stageProblem)
      }
    }
  }

  const seen = new Map<string, number>()
  for (const item of input.items) {
    const key = item.name.trim().toLowerCase()
    seen.set(key, (seen.get(key) ?? 0) + 1)
  }
  const duplicates = input.items
    .map((item) => item.name.trim())
    .filter((name, index, all) => (seen.get(name.toLowerCase()) ?? 0) > 1 && all.indexOf(name) === index)
  if (duplicates.length) errors.push(`Há itens selecionados com o mesmo nome de pasta: ${duplicates.join(", ")}.`)

  return errors
}
